import React, { createContext, useState, useContext, useCallback } from 'react';
import api from '../utils/api';

const ReportContext = createContext();

export const ReportProvider = ({ children }) => {
  const [currentReport, setCurrentReport] = useState(null);
  const [teamStatus, setTeamStatus] = useState(null);
  const [todayDate, setTodayDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch today's date
  const fetchTodayDate = useCallback(async () => {
    try {
      const response = await api.get('/reports/today-date');
      setTodayDate(response.data.date);
      return response.data.date;
    } catch (err) {
      console.error('Error fetching today date:', err);
      return null;
    }
  }, []);

  // Fetch user's report for a date
  const fetchReport = useCallback(async (date) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/reports/get', { params: { date } });
      setCurrentReport(response.data);
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to fetch report';
      setError(errorMsg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Submit or update report
  const submitReport = useCallback(async (yesterdayText, todayText, confirmOffline = false) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/reports/submit', {
        yesterdayText,
        todayText,
        confirmOffline
      });
      
      // If confirmation is required, return the response as-is
      if (response.data.requiresConfirmation) {
        return response.data;
      }
      
      // Otherwise update the local report state
      setCurrentReport(prev => ({
        ...prev,
        yesterdayText,
        todayText,
        status: 'submitted',
        submittedAt: new Date().toISOString()
      }));
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to submit report';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch team status
  const fetchTeamStatus = useCallback(async (date) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/reports/team-status', { params: { date } });
      setTeamStatus(response.data);
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to fetch team status';
      setError(errorMsg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch audit trail
  const fetchAuditTrail = useCallback(async (reportId) => {
    try {
      const response = await api.get(`/reports/${reportId}/audit`);
      return response.data.audit;
    } catch (err) {
      console.error('Error fetching audit trail:', err);
      return [];
    }
  }, []);

  const value = {
    currentReport,
    teamStatus,
    todayDate,
    loading,
    error,
    fetchTodayDate,
    fetchReport,
    submitReport,
    fetchTeamStatus,
    fetchAuditTrail,
    setError
  };

  return (
    <ReportContext.Provider value={value}>
      {children}
    </ReportContext.Provider>
  );
};

export const useReport = () => {
  const context = useContext(ReportContext);
  if (!context) {
    throw new Error('useReport must be used within ReportProvider');
  }
  return context;
};
